import React, { useEffect } from "react";
import Home from "./pages/Home";
import "./assets/styles/global.css";
import "regenerator-runtime/runtime";
import { AuthProvider } from "@/features/auth/context/AuthContext";
import { AppRoutes } from "@/core/router/AppRoutes";
import { ApolloProvider } from "@/lib/apollo/ApolloProvider";
import { ToastContainer } from "react-toastify";
import { useUniversity } from "@/features/auth/hooks/useUniversity";
import FallbackLoader from "@/common/components/shared/FallbackLoader";
import ConnectionIssuesPage from "@/pages/ConnectionIssuesPage";
import { isNetworkFetchError } from "@/features/auth/utils/isNetworkFetchError";
import { initGA } from "@/lib/analytics/ga4";
import { PostHogProvider } from "posthog-js/react";

const options = {
  api_host: "https://us.i.posthog.com",
};

function App() {
  const { university, loading, error } = useUniversity();

  // Initialize Google Analytics with university data
  useEffect(() => {
    if (university) {
      initGA(university);
    } else {
      initGA();
    }
  }, [university]);

  // Update document title with university name
  useEffect(() => {
    const defaultTitle = "Learning Platform";

    if (university && university.name) {
      // Replace HTML entities and sanitize the university name
      const sanitizedName = university.name
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .trim();

      document.title = sanitizedName || defaultTitle;
    } else if (!loading && !error) {
      // Set default title if university data loaded successfully but no named university
      document.title = defaultTitle;
    }

    // Clean up function to reset title if component unmounts
    return () => {
      document.title = defaultTitle;
    };
  }, [university, loading, error]);

  if (loading) {
    return <FallbackLoader message="Initializing.." />;
  }

  if (error) {
    console.log("error: ", error);

    // ISP / DNS blocks (e.g. some providers blocking *.lambda-url.*.on.aws)
    if (isNetworkFetchError(error)) {
      return <ConnectionIssuesPage />;
    }

    return (
      <div className="flex flex-col items-center justify-center w-full min-h-screen px-4 bg-gray-50">
        <div className="w-full max-w-md p-8 text-center bg-white shadow-sm rounded-xl border border-gray-200">
          <p className="text-base text-gray-700">
            {error.message ||
              "Failed to load university configuration. Please try again later."}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  // phc_EkYklhL1w6hppMyti7Q5vrfgcdSpkxPu8sS1ecMDs7T;
  // phc_vxqUbnVSzRh2L6RZgdXISqioeHu6SLLLxnRWaYB2aFZ;
  return (
    <PostHogProvider
      apiKey="phc_EkYklhL1w6hppMyti7Q5vrfgcdSpkxPu8sS1ecMDs7T"
      options={options}
    >
      <AuthProvider university={university}>
        <ApolloProvider>
          <AppRoutes university={university} />
          <ToastContainer newestOnTop theme="colored" position="top-right" />
        </ApolloProvider>
      </AuthProvider>
    </PostHogProvider>
  );
}

export default App;
